/**
 * A tiny byte-mode QR encoder for credential card faces.
 *
 * WHY HAND-ROLLED: package.json carries no QR library, and the card face preview
 * only ever encodes a short opaque credential reference (`crd_…`, ~24 ASCII
 * characters). Versions 1–3 at error-correction level M are single-ECC-block,
 * which keeps the whole spec surface here small enough to verify: no block
 * interleaving, no ECI, no kanji/alphanumeric modes.
 *
 * EVERY CONSTANT IS DERIVED, NOT TRANSCRIBED. GF(256) tables are generated from
 * the field polynomial 0x11D, the Reed–Solomon generator polynomial is computed
 * as Π(x − α^i), and the 15-bit format information is produced by polynomial
 * division with the BCH generator 0x537 and the spec's XOR mask 0x5412. The only
 * memorised numbers are the ones the spec fixes structurally (pad bytes
 * 0xEC/0x11, codeword counts for v1–v3). Tests verify the algebra — the full
 * codeword must divide evenly by the RS generator, and the format bits must be a
 * valid BCH(15,5) codeword — rather than trusting copied tables.
 *
 * If the content does not fit version 3 (42 bytes), `encodeQr` returns null and
 * the caller falls back to printing the reference as text; an unscannable QR on
 * a printed card is worse than an honest string.
 */

/** Codeword geometry for the versions this encoder supports, EC level M. */
const VERSIONS = [
  { version: 1, totalCodewords: 26, eccCodewords: 10 },
  { version: 2, totalCodewords: 44, eccCodewords: 16 },
  { version: 3, totalCodewords: 70, eccCodewords: 26 },
] as const;

// ── GF(256) arithmetic, field polynomial x^8 + x^4 + x^3 + x^2 + 1 (0x11D) ──

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  // Doubled so gfMul never needs an explicit modulo on the exponent sum.
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255];
}

export function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[GF_LOG[a] + GF_LOG[b]];
}

/**
 * Π(x − α^i) for i in 0..degree-1, as descending coefficients with the leading
 * 1 included (poly[0] === 1). Multiplying the accumulator by each monic factor
 * (x + α^i) — minus is plus in GF(2^8) — keeps it monic throughout, which is
 * what lets rsEncode's synthetic division skip a per-step inversion.
 */
export function rsGeneratorPoly(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i++) {
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j++) {
      next[j] ^= poly[j]; // · x
      next[j + 1] ^= gfMul(poly[j], GF_EXP[i]); // · α^i
    }
    poly = next;
  }
  return poly;
}

/** Remainder of message(x)·x^degree divided by the generator polynomial. */
export function rsEncode(data: readonly number[], degree: number): number[] {
  const gen = rsGeneratorPoly(degree);
  const remainder = new Array<number>(degree).fill(0);
  for (const byte of data) {
    const factor = byte ^ remainder[0];
    remainder.shift();
    remainder.push(0);
    for (let i = 0; i < degree; i++) {
      remainder[i] ^= gfMul(gen[i + 1], factor);
    }
  }
  return remainder;
}

/** Polynomial remainder used by tests to prove data‖ecc divides the generator. */
export function rsRemainder(codeword: readonly number[], degree: number): number[] {
  const gen = rsGeneratorPoly(degree);
  const buf = [...codeword, ...new Array<number>(0)];
  for (let i = 0; i + degree < buf.length; i++) {
    const factor = buf[i];
    if (factor === 0) continue;
    for (let j = 0; j <= degree; j++) {
      buf[i + j] ^= gfMul(gen[j], factor);
    }
  }
  return buf.slice(buf.length - degree);
}

// ── Format information: BCH(15,5) over generator 0x537, masked with 0x5412 ──

/** EC level M is 00 in format bits; the mask id occupies the low three bits. */
export function formatBits(mask: number): number {
  const data = (0b00 << 3) | mask; // level M
  let rem = data;
  for (let i = 0; i < 10; i++) {
    rem = (rem << 1) ^ ((rem >> 9) * 0x537);
  }
  return ((data << 10) | (rem & 0x3ff)) ^ 0x5412;
}

// ── Encoding proper ──

export type QrMatrix = boolean[][];

export function encodeQr(text: string): QrMatrix | null {
  const bytes = toBytes(text);
  const spec = VERSIONS.find(
    (v) => v.totalCodewords - v.eccCodewords - 2 >= bytes.length,
  );
  if (!spec) return null;

  const dataCapacity = spec.totalCodewords - spec.eccCodewords;

  // Byte mode segment: 0100, 8-bit count (versions 1–9), then the bytes.
  const bits: number[] = [];
  pushBits(bits, 0b0100, 4);
  pushBits(bits, bytes.length, 8);
  for (const b of bytes) pushBits(bits, b, 8);
  // Terminator, byte alignment, then the spec's alternating pad bytes.
  pushBits(bits, 0, Math.min(4, dataCapacity * 8 - bits.length));
  while (bits.length % 8 !== 0) bits.push(0);
  const dataCodewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j];
    dataCodewords.push(byte);
  }
  for (let pad = 0xec; dataCodewords.length < dataCapacity; pad ^= 0xec ^ 0x11) {
    dataCodewords.push(pad);
  }

  const codewords = [...dataCodewords, ...rsEncode(dataCodewords, spec.eccCodewords)];

  const size = 17 + 4 * spec.version;
  const modules: boolean[][] = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  const isFunction: boolean[][] = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));

  drawFunctionPatterns(modules, isFunction, spec.version, size);
  drawCodewords(modules, isFunction, codewords, size);

  // Try every mask; keep the one the penalty rules like best.
  let bestMask = 0;
  let bestPenalty = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    applyMask(modules, isFunction, mask, size);
    drawFormatBits(modules, mask, size);
    const penalty = penaltyScore(modules, size);
    if (penalty < bestPenalty) {
      bestPenalty = penalty;
      bestMask = mask;
    }
    applyMask(modules, isFunction, mask, size); // XOR is its own inverse
  }
  applyMask(modules, isFunction, bestMask, size);
  drawFormatBits(modules, bestMask, size);
  return modules;
}

/**
 * The dark-module SVG path for a matrix, one `M…h1v1h-1z` square per module,
 * in matrix units. Callers scale via the SVG viewBox; keeping the path unitless
 * lets the same string serve the on-screen preview and the print stylesheet.
 */
export function qrSvgPath(modules: QrMatrix): string {
  const parts: string[] = [];
  for (let y = 0; y < modules.length; y++) {
    for (let x = 0; x < modules[y].length; x++) {
      if (modules[y][x]) parts.push(`M${x} ${y}h1v1h-1z`);
    }
  }
  return parts.join('');
}

function toBytes(text: string): number[] {
  // Credential references are ASCII by construction; TextEncoder keeps any
  // future UTF-8 content correct rather than silently truncating code points.
  return Array.from(new TextEncoder().encode(text));
}

function pushBits(bits: number[], value: number, length: number): void {
  for (let i = length - 1; i >= 0; i--) bits.push((value >> i) & 1);
}

function setFunction(
  modules: boolean[][],
  isFunction: boolean[][],
  y: number,
  x: number,
  dark: boolean,
): void {
  modules[y][x] = dark;
  isFunction[y][x] = true;
}

function drawFunctionPatterns(
  modules: boolean[][],
  isFunction: boolean[][],
  version: number,
  size: number,
): void {
  // Timing patterns.
  for (let i = 0; i < size; i++) {
    setFunction(modules, isFunction, 6, i, i % 2 === 0);
    setFunction(modules, isFunction, i, 6, i % 2 === 0);
  }
  // Finder patterns with their separators, clipped at the edges.
  drawFinder(modules, isFunction, 3, 3, size);
  drawFinder(modules, isFunction, size - 4, 3, size);
  drawFinder(modules, isFunction, 3, size - 4, size);
  // One alignment pattern from version 2 up (v1 has none; v2/v3 centre at size-7).
  if (version >= 2) {
    const c = size - 7;
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        const dark = Math.max(Math.abs(dx), Math.abs(dy)) !== 1;
        setFunction(modules, isFunction, c + dy, c + dx, dark);
      }
    }
  }
  // Reserve the format information areas so codeword placement skips them; the
  // real bits are painted after masking. Includes the always-dark module.
  for (let i = 0; i <= 8; i++) {
    if (i !== 6) {
      setFunction(modules, isFunction, 8, i, false);
      setFunction(modules, isFunction, i, 8, false);
    }
  }
  for (let i = 0; i < 8; i++) {
    setFunction(modules, isFunction, size - 1 - i, 8, false);
    setFunction(modules, isFunction, 8, size - 8 + i, false);
  }
}

function drawFinder(
  modules: boolean[][],
  isFunction: boolean[][],
  cy: number,
  cx: number,
  size: number,
): void {
  for (let dy = -4; dy <= 4; dy++) {
    for (let dx = -4; dx <= 4; dx++) {
      const y = cy + dy;
      const x = cx + dx;
      if (y < 0 || y >= size || x < 0 || x >= size) continue;
      const dist = Math.max(Math.abs(dx), Math.abs(dy));
      setFunction(modules, isFunction, y, x, dist !== 2 && dist !== 4);
    }
  }
}

function drawCodewords(
  modules: boolean[][],
  isFunction: boolean[][],
  codewords: readonly number[],
  size: number,
): void {
  let bitIndex = 0;
  const totalBits = codewords.length * 8;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5; // the vertical timing column is skipped whole
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? size - 1 - vert : vert;
        if (isFunction[y][x] || bitIndex >= totalBits) continue;
        modules[y][x] = ((codewords[bitIndex >> 3] >> (7 - (bitIndex & 7))) & 1) !== 0;
        bitIndex++;
        // Any leftover modules (remainder bits) stay light, which is the spec's
        // required zero fill.
      }
    }
  }
}

function applyMask(
  modules: boolean[][],
  isFunction: boolean[][],
  mask: number,
  size: number,
): void {
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (isFunction[y][x]) continue;
      let invert = false;
      switch (mask) {
        case 0: invert = (x + y) % 2 === 0; break;
        case 1: invert = y % 2 === 0; break;
        case 2: invert = x % 3 === 0; break;
        case 3: invert = (x + y) % 3 === 0; break;
        case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
        case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break;
        case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
        default: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
      }
      if (invert) modules[y][x] = !modules[y][x];
    }
  }
}

function drawFormatBits(modules: boolean[][], mask: number, size: number): void {
  const bits = formatBits(mask);
  const bit = (i: number) => ((bits >> i) & 1) !== 0;
  // First copy, around the top-left finder.
  for (let i = 0; i <= 5; i++) modules[8][i] = bit(i);
  modules[8][7] = bit(6);
  modules[8][8] = bit(7);
  modules[7][8] = bit(8);
  for (let i = 9; i < 15; i++) modules[14 - i][8] = bit(i);
  // Second copy, split between the two other finders.
  for (let i = 0; i <= 7; i++) modules[size - 1 - i][8] = bit(i);
  for (let i = 8; i < 15; i++) modules[8][size - 15 + i] = bit(i);
  // The module the spec keeps dark unconditionally.
  modules[size - 8][8] = true;
}

/** ISO 18004 penalty rules N1–N4; lower is better. */
function penaltyScore(modules: boolean[][], size: number): number {
  let score = 0;
  // N1: runs of five-plus same-coloured modules, rows and columns.
  for (let axis = 0; axis < 2; axis++) {
    for (let a = 0; a < size; a++) {
      let run = 1;
      let prev = at(modules, axis, a, 0);
      for (let b = 1; b < size; b++) {
        const cur = at(modules, axis, a, b);
        if (cur === prev) {
          run++;
          if (run === 5) score += 3;
          else if (run > 5) score += 1;
        } else {
          prev = cur;
          run = 1;
        }
      }
    }
  }
  // N2: 2x2 blocks of one colour.
  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const c = modules[y][x];
      if (c === modules[y][x + 1] && c === modules[y + 1][x] && c === modules[y + 1][x + 1]) {
        score += 3;
      }
    }
  }
  // N3: finder-like 1:1:3:1:1 pattern with 4-module light flank, either side.
  for (let axis = 0; axis < 2; axis++) {
    for (let a = 0; a < size; a++) {
      let bitsStr = '';
      for (let b = 0; b < size; b++) bitsStr += at(modules, axis, a, b) ? '1' : '0';
      for (
        let idx = bitsStr.indexOf('10111010000');
        idx !== -1;
        idx = bitsStr.indexOf('10111010000', idx + 1)
      ) {
        score += 40;
      }
      for (
        let idx = bitsStr.indexOf('00001011101');
        idx !== -1;
        idx = bitsStr.indexOf('00001011101', idx + 1)
      ) {
        score += 40;
      }
    }
  }
  // N4: dark-module balance, 10 points per 5% step away from 50%.
  let dark = 0;
  for (const row of modules) for (const m of row) if (m) dark++;
  const percent = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;
  return score;
}

function at(modules: boolean[][], axis: number, a: number, b: number): boolean {
  return axis === 0 ? modules[a][b] : modules[b][a];
}
